// VictoryBanner — full-bleed end-of-match overlay shown when a player has
// captured every city. Dismissible (visually, not the underlying state) so
// the user can still inspect the final board. The card paints victory/defeat
// art under the theme's `--panel-scrim`, so it follows the app theme: cream
// over the art in light mode, ink in dark mode (the same scrim value it
// hardcoded before, so dark mode is unchanged).

import type { PresetId } from '@aod/protocol';
import { Button } from '@aod/ui/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@aod/ui/components/ui/card';
import { BarChart3, Crown, GraduationCap, Home, Rocket, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import {
  type LadderId,
  isLadderId,
  isSuggested,
  markSuggested,
  nextPresetId,
  presetDelta,
} from '../lib/graduation.js';
import { useSettings } from '../settings/useSettings.js';
import { formatList, moduleLabel, presetName } from '../setup/presentation.js';
import type { VictoryPath } from '../sim/useSimWorld/types.js';

interface VictoryBannerProps {
  readonly winner: number;
  readonly path: VictoryPath;
  readonly localPlayerIndex: number;
  readonly onNewMatch: () => void;
  readonly onReturnToMenu?: () => void;
  readonly onViewSummary?: () => void;
  /** Preset the match was played under (rulesets R8 graduation), if any. */
  readonly playedPreset?: PresetId | null;
  /** Open the setup screen preloaded with the suggested next preset. */
  readonly onTryPreset?: (id: LadderId) => void;
}

const PATH_LABEL: Record<VictoryPath, MessageKey> = {
  conquest: 'victory.title.conquest',
  economy: 'victory.title.economy',
  science: 'victory.title.science',
  faith: 'victory.title.faith',
  diplomacy: 'victory.title.diplomacy',
  milestones: 'victory.title.milestones',
};

const PATH_DESCRIPTION: Record<VictoryPath, MessageKey> = {
  conquest: 'victory.desc.conquest',
  economy: 'victory.desc.economy',
  science: 'victory.desc.science',
  faith: 'victory.desc.faith',
  diplomacy: 'victory.desc.diplomacy',
  milestones: 'victory.desc.milestones',
};

export function VictoryBanner({
  winner,
  path,
  localPlayerIndex,
  onNewMatch,
  onReturnToMenu,
  onViewSummary,
  playedPreset,
  onTryPreset,
}: VictoryBannerProps) {
  const t = useT();
  const locale = useSettings((s) => s.locale);
  const [dismissed, setDismissed] = useState(false);
  // Spec §10 graduation: a local win on a ladder preset suggests the next
  // one — once per transition (localStorage), inside the same dismissible
  // banner. Hooks stay above the early return.
  const played = playedPreset && isLadderId(playedPreset) ? playedPreset : null;
  const next = played ? nextPresetId(played) : null;
  const showGraduation =
    winner === localPlayerIndex && played !== null && next !== null && !isSuggested(played);
  const delta = showGraduation && played && next ? presetDelta(played, next) : [];
  useEffect(() => {
    if (showGraduation && played) markSuggested(played);
  }, [showGraduation, played]);
  if (dismissed) return null;
  const isYou = winner === localPlayerIndex;

  return (
    <div
      role="alertdialog"
      aria-modal="false"
      aria-labelledby="victory-title"
      data-coachmark-target="match-end"
      data-testid="victory-banner"
      {...appDom({
        id: 'victory-banner',
        type: 'Banner',
        scope: 'banner',
        label: t('victory.matchResult'),
      })}
      className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
    >
      <Card
        className="pointer-events-auto w-[min(92vw,420px)] border-amber-500/50 text-card-foreground shadow-2xl backdrop-blur animate-fade-in-scale dark:border-amber-400/60"
        style={{
          background: isYou
            ? `linear-gradient(var(--panel-scrim), var(--panel-scrim)), url('/assets/images/ui/victory_bg.webp')`
            : `linear-gradient(var(--panel-scrim), var(--panel-scrim)), url('/assets/images/ui/defeat_bg.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div
            className="flex size-10 items-center justify-center rounded-md bg-amber-500/15 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300"
            aria-hidden
          >
            <Crown className="size-6" />
          </div>
          <div className="flex-1">
            <CardTitle id="victory-title" className="text-base">
              {isYou ? t('victory.youWin') : t('common.playerWins', { player: winner + 1 })}
            </CardTitle>
            <p className="mt-1 text-[12px] text-muted-foreground">{t(PATH_LABEL[path])}</p>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={t('victory.dismiss')}
            className="size-7"
            onClick={() => setDismissed(true)}
          >
            <X className="size-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="text-[12px] text-muted-foreground">
          {t(PATH_DESCRIPTION[path])} {t('victory.paused')}
          {showGraduation && played && next && (
            <div
              data-testid="victory-graduation"
              className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-card-foreground dark:border-amber-400/40 dark:bg-amber-400/10"
            >
              <p className="flex items-center gap-2 text-[13px] font-semibold text-amber-700 dark:text-amber-300">
                <GraduationCap className="size-4" aria-hidden />
                {t('victory.graduation.mastered', { preset: presetName(t, played) })}
              </p>
              {delta.length > 0 && (
                <p className="mt-1 text-muted-foreground">
                  {t('victory.graduation.adds', {
                    list: formatList(
                      locale,
                      delta.map((m) => moduleLabel(t, m)),
                    ),
                  })}
                </p>
              )}
              {onTryPreset && (
                <Button
                  type="button"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => onTryPreset(next)}
                  aria-label={t('victory.graduation.tryNext', { next: presetName(t, next) })}
                >
                  <Rocket className="size-4" aria-hidden />
                  {t('victory.graduation.tryNext', { next: presetName(t, next) })}
                </Button>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="gap-2 flex-wrap">
          {onViewSummary && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onViewSummary}
              aria-label={t('victory.viewSummaryLabel')}
            >
              <BarChart3 className="size-4" aria-hidden /> {t('victory.viewSummary')}
            </Button>
          )}
          <div className="flex gap-2 w-full">
            {onReturnToMenu && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onReturnToMenu}
                aria-label={t('menu.game.mainMenu')}
              >
                <Home className="size-4" aria-hidden /> {t('victory.menu')}
              </Button>
            )}
            <Button
              type="button"
              className="flex-1"
              onClick={onNewMatch}
              aria-label={t('victory.newMatchLabel')}
            >
              <RotateCcw className="size-4" aria-hidden /> {t('menu.game.newMatch')}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
